import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LuCalendarDays,
  LuCamera,
  LuPencil,
  LuTrash2,
  LuUpload,
  LuUserRound,
  LuUsersRound,
} from "react-icons/lu";
import { userService } from "../../services/userService";
import ReusableDatePicker from "../../components/ReusableDatePicker";
import EditIcon from "../../icons/EditIcon";
import userController from "../../controllers/userController";
import Utils from "../../utils/Utils";
import AccountActionButton from "./AccountActionButton";
import { translateFieldLabel, translateText, translateLabel, useTranslationSnapshot } from "./../../translations/runtime";


const EMPTY_USER_INFO = {
  firstName: "",
  lastName: "",
  gender: "",
  birthdate: "",
  profilePicture: "",
};

const avatarActions = [
  { icon: LuUpload, label: "Upload photo" },
  { icon: LuCamera, label: "Take photo" },
  // { icon: LuPencil, label: "Edit photo" },
  { icon: LuTrash2, label: "Delete photo" },
];

const getResponseData = (response) => {
  return (
    response?.data?.data ??
    response?.data?.result ??
    response?.data ??
    response ??
    {}
  );
};

const toDateInput = (value) => {
  if (!value) return "";

  const stringValue = String(value);
  const dateParts = stringValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (dateParts) {
    return `${dateParts[3]}-${dateParts[2]}-${dateParts[1]}`;
  }

  return stringValue;
};

const toApiBirthdate = (value) => {
  if (!value) return "";

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const toFormGender = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  if (normalized === "MALE") return "Male";
  if (normalized === "FEMALE") return "Female";

  return value || "";
};

const toApiGender = (value, currentGender = {}) => {
  const genderName = String(value || "")
    .trim()
    .toUpperCase();
  const fallbackPronoun = genderName === "FEMALE" ? "HER" : "HIS";

  return {
    costumGenderName:
      genderName === "CUSTOM"
        ? currentGender?.costumGenderName || "Other"
        : currentGender?.costumGenderName || "",
    genderName,
    pronoun:
      currentGender?.pronoun ||
      (genderName === "MALE" || genderName === "FEMALE" ? fallbackPronoun : ""),
  };
};

const getImageExtension = (file) => {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();

  if (type.includes("png") || name.endsWith(".png")) return "png";
  if (type.includes("webp") || name.endsWith(".webp")) return "webp";
  if (type.includes("gif") || name.endsWith(".gif")) return "gif";
  return "jpg";
};

const getSafeFileName = (value, fallback = "profile-picture") => {
  const normalized = String(value || fallback)
    .trim()
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return normalized || fallback;
};

const getCountryName = (user = {}) =>
  String(
    user?.countryName ||
      user?.countryOfResidence?.countryName ||
      user?.countryOfResidence ||
      user?.address?.countryName ||
      user?.address?.country ||
      "",
  ).trim();

const getProfileUserName = (user = {}, fallback = "") =>
  String(
    user?.username ||
      user?.userName ||
      user?.name ||
      `${user?.firstname || ""} ${user?.lastname || ""}`.trim() ||
      fallback ||
      "",
  ).trim();

const mapUserResponseToForm = (user = {}) => ({
  firstName: user.firstname || "",
  lastName: user.lastname || "",
  gender: toFormGender(
    user.gender?.genderName || user.gender?.costumGenderName || "",
  ),
  birthdate: toDateInput(user.birthdate),
  profilePicture: user.profilePicture || user.profilePic || "",
});

const getInitials = (firstName, lastName) => {
  const first = String(firstName || "")
    .trim()
    .charAt(0);

  const last = String(lastName || "")
    .trim()
    .charAt(0);

  return `${first}${last}`.toUpperCase() || "U";
};

const getJwtFromStoredUser = (storedUser) => {
  if (typeof storedUser === "string") {
    return storedUser;
  }

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getUserIdFromPayload = (payload = {}) =>
  String(
    payload?.id ||
      payload?.userId ||
      payload?.userID ||
      payload?.UserId ||
      payload?.UserID ||
      payload?.sub ||
      payload?.user?.id ||
      payload?.user?.userId ||
      payload?.user?.userID ||
      "",
  ).trim();

const resolveAuthenticatedSession = ({
  jwt: jwtProp,
  userID: userIDProp,
} = {}) => {
  const storedUser = userService.getJwt();

  const jwt = String(jwtProp || getJwtFromStoredUser(storedUser) || "").trim();

  const userID = String(
    userIDProp || userService.getUserData().UserID || "",
  ).trim();

  return {
    jwt,
    userID,
  };
};

function UserInfoField({ icon: Icon, label, value }) {
  useTranslationSnapshot();
  return (
    <div className="vendor-account-info-field">
      <div className="vendor-account-info-label">
        <Icon aria-hidden="true" />

        <span>
          {translateText(label)}
          <i>*</i>
        </span>
      </div>

      <p>{value || "-"}</p>
    </div>
  );
}

function UserInformationPanel({ jwt, userID }) {
  useTranslationSnapshot();
  const authenticatedSession = useMemo(
    () =>
      resolveAuthenticatedSession({
        jwt,
        userID,
      }),
    [jwt, userID],
  );

  const resolvedJwt = authenticatedSession.jwt;

  const resolvedUserID = authenticatedSession.userID;

  const authPayload = useMemo(
    () => (resolvedJwt ? Utils.parseJwt(resolvedJwt) || {} : {}),
    [resolvedJwt],
  );

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);

  const [isAvatarSheetOpen, setIsAvatarSheetOpen] = useState(false);

  const [userInfo, setUserInfo] = useState(EMPTY_USER_INFO);

  const [userInfoForm, setUserInfoForm] = useState(EMPTY_USER_INFO);

  const [userApiData, setUserApiData] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const [error, setError] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const [selectedProfileImage, setSelectedProfileImage] = useState(null);

  const [profileImagePreview, setProfileImagePreview] = useState("");

  const [isProfileImageDeleted, setIsProfileImageDeleted] = useState(false);

  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const [isCameraStarting, setIsCameraStarting] = useState(false);

  const initials = useMemo(
    () => getInitials(userInfoForm.firstName, userInfoForm.lastName),
    [userInfoForm.firstName, userInfoForm.lastName],
  );

  const avatarPreview =
    profileImagePreview ||
    (!isProfileImageDeleted ? userInfoForm.profilePicture : "");

  const stopCameraTracks = useCallback(() => {
    if (!cameraStreamRef.current) return;

    cameraStreamRef.current.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
  }, []);

  useEffect(() => {
    if (videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [isCameraOpen]);

  useEffect(() => {
    return () => {
      stopCameraTracks();
    };
  }, [stopCameraTracks]);

  // =====================================================
  // GET USER INFORMATION
  // =====================================================

  const loadUserInformation = async () => {
    if (!resolvedJwt || !resolvedUserID) {
      console.error("[USER INFO] Missing session", {
        userID: resolvedUserID,
        hasJwt: !!resolvedJwt,
      });

      setError(
        "Unable to load user information. Missing authenticated session.",
      );

      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const response = await userController.getUser({
        userID: resolvedUserID,
        jwt: resolvedJwt,
      });

      const user = getResponseData(response);

      const mappedUser = mapUserResponseToForm(user);

      setUserApiData(user);
      setUserInfo(mappedUser);
      setUserInfoForm(mappedUser);
    } catch (requestError) {
      console.error("[USER INFO] GET FAILED:", requestError);

      console.error("[USER INFO] GET STATUS:", requestError?.response?.status);

      console.error("[USER INFO] GET RESPONSE:", requestError?.response?.data);

      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          requestError?.message ||
          "Failed to load user information.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserInformation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUserID, resolvedJwt]);

  // =====================================================
  // FORM
  // =====================================================

  const updateUserInfoField = (field, value) => {
    setSuccessMessage("");
    setError("");
    const nextValue =
      field === "firstName" || field === "lastName"
        ? Utils.sanitizeNameInput(value)
        : value;

    setUserInfoForm((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  const clearPendingProfileImage = () => {
    setSelectedProfileImage(null);
    setProfileImagePreview("");
    setIsProfileImageDeleted(false);
  };

  const startEditing = () => {
    setUserInfoForm(userInfo);
    clearPendingProfileImage();
    setError("");
    setSuccessMessage("");
    setIsEditing(true);
  };

  const closeEdit = () => {
    stopCameraTracks();
    setUserInfoForm(userInfo);
    clearPendingProfileImage();
    setIsEditing(false);
    setIsAvatarSheetOpen(false);
    setIsCameraOpen(false);
    setError("");
    setSuccessMessage("");
  };

  const openFilePicker = () => {
    setIsAvatarSheetOpen(false);
    fileInputRef.current?.click();
  };

  const prepareProfileImage = async (file) => {
    const validation = Utils.validateImageFile(file);

    if (!validation.valid) {
      throw new Error(validation.message);
    }

    const dataUrl = await Utils.fileToBase64(file);
    const base64 = String(dataUrl || "")
      .replace(/^data:[^;]+;base64,/i, "")
      .replace(/\s+/g, "");

    if (!base64) {
      throw new Error("Selected image could not be read.");
    }

    const extension = getImageExtension(file);
    const safeName = getSafeFileName(file.name);
    const fileName = `${safeName}.${extension}`;

    setSelectedProfileImage({
      base64,
      extension,
      fileName,
    });
    setProfileImagePreview(dataUrl);
    setIsProfileImageDeleted(false);
    setUserInfoForm((current) => ({
      ...current,
      profilePicture: dataUrl,
    }));
  };

  const handleProfileFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    try {
      setError("");
      setSuccessMessage("");
      await prepareProfileImage(file);
    } catch (fileError) {
      setError(fileError?.message || "Could not prepare this image.");
    }
  };

  const handleDeleteProfileImage = () => {
    setIsAvatarSheetOpen(false);
    setSelectedProfileImage(null);
    setProfileImagePreview("");
    setIsProfileImageDeleted(true);
    setUserInfoForm((current) => ({
      ...current,
      profilePicture: "",
    }));
    setSuccessMessage("");
    setError("");
  };

  const openCamera = async () => {
    setIsAvatarSheetOpen(false);
    setError("");
    setSuccessMessage("");

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Camera is not available in this browser.");
      return;
    }

    try {
      setIsCameraStarting(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
        },
        audio: false,
      });

      cameraStreamRef.current = stream;
      setIsCameraOpen(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (cameraError) {
      setError(cameraError?.message || "Camera permission was denied.");
    } finally {
      setIsCameraStarting(false);
    }
  };

  const closeCamera = () => {
    stopCameraTracks();
    setIsCameraOpen(false);
    setIsCameraStarting(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      setError("Camera is not ready yet.");
      return;
    }

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const base64 = dataUrl.replace(/^data:[^;]+;base64,/i, "");

    setSelectedProfileImage({
      base64,
      extension: "jpg",
      fileName: `profile-picture-${Date.now()}.jpg`,
    });
    setProfileImagePreview(dataUrl);
    setIsProfileImageDeleted(false);
    setUserInfoForm((current) => ({
      ...current,
      profilePicture: dataUrl,
    }));

    closeCamera();
  };

  const handleAvatarAction = (label) => {
    if (label === "Upload photo" || label === "Edit photo") {
      openFilePicker();
      return;
    }

    if (label === "Take photo") {
      openCamera();
      return;
    }

    if (label === "Delete photo") {
      handleDeleteProfileImage();
    }
  };

  const validateForm = () => {
    if (!String(userInfoForm.firstName || "").trim()) {
      setError("First name is required.");
      return false;
    }

    if (!Utils.checkName(userInfoForm.firstName)) {
      setError("First name can only contain letters.");
      return false;
    }

    if (!String(userInfoForm.lastName || "").trim()) {
      setError("Last name is required.");
      return false;
    }

    if (!Utils.checkName(userInfoForm.lastName)) {
      setError("Last name can only contain letters.");
      return false;
    }

    if (!String(userInfoForm.gender || "").trim()) {
      setError("Gender is required.");
      return false;
    }

    if (!userInfoForm.birthdate) {
      setError("Birthdate is required.");
      return false;
    }

    return true;
  };

  // =====================================================
  // PUT UPDATE USER
  // =====================================================

  const handleSaveChanges = async (event) => {
    event?.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!resolvedJwt) {
      setError("Missing authenticated session.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");

      /*
       * IMPORTANT:
       * Prefer the ID returned from GET.
       * If GET did not return one, use
       * the resolved authenticated user ID.
       */
      const actualUserId = userApiData?.id ?? resolvedUserID;

      const existingProfilePicture =
        userApiData?.profilePicture || userApiData?.profilePic || "";

      const actualCountryName =
        getCountryName(userApiData) || getCountryName(authPayload);

      const actualUserName = getProfileUserName(
        userApiData,
        getProfileUserName(authPayload, resolvedUserID),
      );

      /*
       * Swagger:
       *
       * PUT /api/user/v1/updateUser
       *
       * {
       *   birthdate,
       *   firstname,
       *   gender,
       *   id,
       *   lastname,
       *   phoneNumber,
       *   profilePic
       * }
       */

      const userUpdateRequest = {
        birthdate: toApiBirthdate(userInfoForm.birthdate),

        firstname: String(userInfoForm.firstName || "").trim(),

        gender: toApiGender(userInfoForm.gender, userApiData?.gender),

        id: actualUserId,

        lastname: String(userInfoForm.lastName || "").trim(),

        phoneNumber: userApiData?.phoneNumber ?? null,

        profilePic: isProfileImageDeleted ? "" : existingProfilePicture,
      };

      if (selectedProfileImage) {
        userUpdateRequest.createCustomBucketObjectDTO = {
          attachmentStatus: "DEFAULT",
          attachmentType: "ProfilePicture",
          countryName: actualCountryName,
          customPath: "",
          data: selectedProfileImage.base64,
          extension: selectedProfileImage.extension,
          objectName: selectedProfileImage.fileName,
          userName: actualUserName,
        };
      }

      const response = await userController.updateUser({
        jwt: resolvedJwt,
        userUpdateRequest,
      });

      /*
       * Reload user from backend after
       * successful PUT.
       */
      await loadUserInformation();

      setIsEditing(false);
      setIsAvatarSheetOpen(false);
      clearPendingProfileImage();

      setSuccessMessage("User information updated successfully.");
    } catch (requestError) {
      console.error("====================================");

      console.error("[UPDATE USER] FAILED");

      console.error("[UPDATE USER] STATUS:", requestError?.response?.status);

      console.error("[UPDATE USER] RESPONSE:", requestError?.response?.data);

      console.error("[UPDATE USER] FULL ERROR:", requestError);

      console.error("====================================");

      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          requestError?.message ||
          "Failed to update user information.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // =====================================================
  // DISPLAY FIELDS
  // =====================================================

  const genderLabelId = {
    Male: "txt_end_user_male",
    Female: "txt_end_user_female",
  }[userInfo.gender];

  const userInfoDisplayFields = [
    {
      icon: LuUserRound,
      label: "First Name",
      value: userInfo.firstName,
    },
    {
      icon: LuUserRound,
      label: "Last Name",
      value: userInfo.lastName,
    },
    {
      icon: LuUsersRound,
      label: "Gender",
      value: genderLabelId ? translateLabel(genderLabelId, userInfo.gender) : userInfo.gender,
    },
    {
      icon: LuCalendarDays,
      label: "Birthdate",
      value: userInfo.birthdate || "DD/MM/YYYY",
    },
  ];

  if (isLoading && !userApiData) {
    return (
      <div className="vendor-account-info">
        <h2>{translateLabel("lnk_user_information", "User Information")}</h2>

        <p>{translateLabel("txt_web_vendor_loading_user_information", "Loading user information...")}</p>
      </div>
    );
  }

  return (
    <div className="vendor-account-info">
      <input
        ref={fileInputRef}
        accept="image/*"
        aria-hidden="true"
        onChange={handleProfileFileChange}
        style={{ display: "none" }}
        tabIndex={-1}
        type="file"
      />

      <button
        aria-label={translateLabel("txt_web_vendor_edit_profile", "Edit profile")}
        className="vendor-account-info-edit"
        onClick={startEditing}
        type="button"
        disabled={isLoading || isSaving}
      >
        <EditIcon size={35} />
      </button>

      <h2>{translateLabel("lnk_user_information", "User Information")}</h2>

      <p>{translateLabel("txt_user_information_description", "View your personal information and edit your profile picture.")}</p>

      {error ? <p className="vendor-account-info-error">{translateText(error)}</p> : null}

      {successMessage ? (
        <p className="vendor-account-info-success">{translateText(successMessage)}</p>
      ) : null}

      <div className="vendor-account-info-avatar-wrap">
        {avatarPreview ? (
          <img
            alt={translateLabel("txt_web_vendor_profile", "Profile")}
            className="vendor-account-info-avatar"
            src={avatarPreview}
          />
        ) : (
          <div aria-hidden="true" className="vendor-account-info-avatar">
            {initials}
          </div>
        )}

        {isEditing ? (
          <button
            aria-label={translateLabel("txt_web_vendor_edit_avatar", "Edit avatar")}
            className="vendor-account-info-avatar-edit"
            onClick={() => setIsAvatarSheetOpen(true)}
            type="button"
            disabled={isSaving}
          > {translateLabel("txt_web_vendor_edit", "Edit")} </button>
        ) : null}
      </div>

      {isEditing ? (
        <form className="vendor-account-info-form" onSubmit={handleSaveChanges}>
          <div className="vendor-account-info-form-grid">
            <label className="vendor-account-info-input-field">
              <span className="vendor-account-info-label">
                <LuUserRound aria-hidden="true" />

                <span> {translateLabel("input_first_name", "First Name")}<i>*</i>
                </span>
              </span>

              <input
                type="text"
                value={userInfoForm.firstName}
                disabled={isSaving}
                onChange={(event) =>
                  updateUserInfoField("firstName", event.target.value)
                }
              />
            </label>

            <label className="vendor-account-info-input-field">
              <span className="vendor-account-info-label">
                <LuUserRound aria-hidden="true" />

                <span> {translateLabel("input_last_name", "Last Name")}<i>*</i>
                </span>
              </span>

              <input
                type="text"
                value={userInfoForm.lastName}
                disabled={isSaving}
                onChange={(event) =>
                  updateUserInfoField("lastName", event.target.value)
                }
              />
            </label>

            <label className="vendor-account-info-input-field vendor-account-info-input-wide">
              <span className="vendor-account-info-label">
                <LuUsersRound aria-hidden="true" />

                <span> {translateLabel("input_gender", "Gender")}<i>*</i>
                </span>
              </span>

              <select
                value={userInfoForm.gender}
                disabled={isSaving}
                onChange={(event) =>
                  updateUserInfoField("gender", event.target.value)
                }
              >
                <option value="">{translateLabel("txt_web_vendor_select_gender", "Select gender")}</option>

                <option value="Male">{translateLabel("txt_end_user_male", "Male")}</option>

                <option value="Female">{translateLabel("txt_end_user_female", "Female")}</option>
              </select>
            </label>

            <label className="vendor-account-info-input-field vendor-account-info-input-wide">
              <span className="vendor-account-info-label">
                <LuCalendarDays aria-hidden="true" />

                <span> {translateLabel("input_birthdate", "Birthdate")}<i>*</i>
                </span>
              </span>

              <ReusableDatePicker
                ariaLabel={translateFieldLabel("input_birthdate", "Birthdate")}
                value={userInfoForm.birthdate}
                onChange={(value) => updateUserInfoField("birthdate", value)}
              />
            </label>
          </div>

          <div className="vendor-account-info-actions">
            <AccountActionButton
              width="124px"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? translateLabel("txt_end_user_saving", "Saving...") : translateLabel("btn_save_changes", "Save Changes")}
            </AccountActionButton>

            <AccountActionButton
              color="dark"
              onClick={closeEdit}
              type="button"
              disabled={isSaving}
            > {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
          </div>
        </form>
      ) : (
        <div className="vendor-account-info-grid">
          {userInfoDisplayFields.map((field) => (
            <UserInfoField {...field} key={field.label} />
          ))}
        </div>
      )}

      {isAvatarSheetOpen ? (
        <div className="vendor-avatar-sheet" role="dialog" aria-modal="true">
          <button
            aria-label={translateLabel("txt_web_vendor_close_photo_actions", "Close photo actions")}
            className="vendor-avatar-sheet-backdrop"
            onClick={() => setIsAvatarSheetOpen(false)}
            type="button"
          />

          <div className="vendor-avatar-sheet-panel">
            {avatarActions.map(({ icon: Icon, label }) => (
              <button
                className="vendor-avatar-sheet-action"
                key={label}
                type="button"
                disabled={isSaving}
                onClick={() => handleAvatarAction(label)}
              >
                <Icon aria-hidden="true" />

                <span>{translateText(label)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {isCameraOpen ? (
        <div className="vendor-avatar-sheet" role="dialog" aria-modal="true">
          <button
            aria-label={translateLabel("txt_web_vendor_close_camera", "Close camera")}
            className="vendor-avatar-sheet-backdrop"
            onClick={closeCamera}
            type="button"
          />

          <div className="vendor-avatar-sheet-panel">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                background: "#000000",
                borderRadius: "8px",
                maxWidth: "320px",
                width: "100%",
              }}
            />

            <canvas ref={canvasRef} style={{ display: "none" }} />

            <div className="vendor-account-info-actions">
              <AccountActionButton
                onClick={capturePhoto}
                type="button"
                disabled={isCameraStarting}
              > {translateLabel("txt_web_vendor_capture", "Capture")} </AccountActionButton>

              <AccountActionButton
                color="dark"
                onClick={closeCamera}
                type="button"
              > {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UserInformationPanel;
